import { useRef, useState } from 'react';
import styled from 'styled-components';

import { usePhotoVenueTag } from '../../../tag-manager/venue.js';
import { venuePhotoAlt } from '../../venue-photo-alt';
import ImageCarousel from '../../Shared/ImageCarousel.jsx';
import { ImagesIcon, mobile, tablet } from '../../styles';

const ImageContent = styled.div`
  position: relative;
  margin: 32px 0;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  @media (max-width: ${tablet}) {
    margin: 24px 0;
    gap: 7px;
  }

  @media (max-width: ${mobile}) {
    margin: 15px 0 18px;
  }

  & > div {
    flex: 0 0 30%;

    button {
      display: block;
      width: 100%;
      margin: 0;
      padding: 0;
      border: none;
      background-color: transparent;

      &:focus:not(:focus-visible) {
        outline: none;
      }
    }
  }

  & > div:first-child {
    flex-grow: 1;

    img {
      height: 322px;

      @media (max-width: ${tablet}) {
        height: 257px;
      }

      @media (max-width: ${mobile}) {
        height: 180px;
      }
    }
  }

  & > div:last-child {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    img {
      height: 157px;

      @media (max-width: ${tablet}) {
        height: 125px;
      }

      @media (max-width: ${mobile}) {
        height: 87px;
      }
    }
  }

  img {
    border-radius: 4px;
    width: 100%;
    object-fit: cover;
  }
`;

const MorePhotos = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 9px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(5px);

  svg {
    display: inline-block;
    height: 14px;
    width: 14px;
    margin-top: -2px;
  }
`;

// V5: the dark page behind the full-screen viewer. The viewer sets its own
// size (ImageCarousel), so this only centres it.
const CarouselWrapper = styled.div`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// AEO-08a: takes the whole venue, not three fields off it, because the alt
// text needs the setting, the type and the city as well as the name.
const Photos = ({ venue }) => {
  const [showImage, setShowImage] = useState(-1);
  const ref = useRef(null);

  const { name, id: venueId } = venue || {};
  const photos = Array.isArray(venue?.photos) ? venue.photos : [];

  if (!photos.length) {
    return null;
  }

  const [photo1, photo2, photo3, ...more] = photos;
  const hasMore = !!more.length;
  // The gallery shows three pictures of one venue. Without the position they
  // would carry three identical sentences, so a parser could not tell them
  // apart. The count is the FULL set, which is what the "+ N" badge opens.
  const altFor = (index) => venuePhotoAlt(venue, { index, total: photos.length });

  return (
    <>
      <ImageContent>
        <div>
          {!!photo1 && (
            <button
              onClick={() => {
                setShowImage(0);
                usePhotoVenueTag({ index: 0, totalPhotos: photos.length, venueName: name, venueId: venueId });
              }}
            >
              <img src={photo1.thumbnail} alt={altFor(0)} />
            </button>
          )}
        </div>
        <div>
          {!!photo2 && (
            <>
              <button
                onClick={() => {
                  setShowImage(1);
                  usePhotoVenueTag(1, photos.length);
                }}
              >
                <img src={photo2.thumbnail} alt={altFor(1)} />
              </button>
              {!!photo3 && (
                <button
                  onClick={() => {
                    setShowImage(2);
                    usePhotoVenueTag(2, photos.length);
                  }}
                >
                  <img src={photo3.thumbnail} alt={altFor(2)} />
                </button>
              )}
              {hasMore && (
                <MorePhotos>
                  <ImagesIcon />+ {more.length}
                </MorePhotos>
              )}
            </>
          )}
        </div>
      </ImageContent>
      {showImage >= 0 && (
        <CarouselWrapper
          ref={ref}
          onClick={(e) => {
            // close if background element clicked directly (and not bubbled)
            if (e.target === ref.current) {
              setShowImage(-1);
            }
          }}
        >
          <ImageCarousel
            fullScreen
            images={photos.map((photo) => photo.url)}
            alts={photos.map((_, index) => altFor(index))}
            defaultIndex={showImage}
            onClose={() => setShowImage(-1)}
          />
        </CarouselWrapper>
      )}
    </>
  );
};

export default Photos;
